import { Shield, Globe, Users, TrendingUp, HeartHandshake, Building2, UserCheck, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import PublicShell from '@/components/layouts/PublicShell';
import SplitHero from '@/components/public/SplitHero';
import SectionHeading from '@/components/public/SectionHeading';
import FeatureCard from '@/components/public/FeatureCard';
import SplitFeature from '@/components/public/SplitFeature';
import CtaCard from '@/components/public/CtaCard';
import PhotoPanel from '@/components/public/PhotoPanel';

const SERVICE_LINKS = [
  { to: '/anlageberatung', title: 'Anlageberatung', desc: 'Individuell und zielgerichtet' },
  { to: '/altersvorsorge', title: 'Altersvorsorge', desc: 'Finanzielle Sicherheit im Ruhestand' },
  { to: '/finanzberatung-frauen', title: 'Finanzberatung für Frauen', desc: 'Fokus auf besondere Lebenssituationen' },
  { to: '/finanzierungen', title: 'Finanzierungen', desc: 'Lösungen für private und berufliche Vorhaben' },
  { to: '/advisory', title: 'Advisory für Vermögensverwaltungen', desc: 'Strategische Begleitung auf höchstem Niveau' },
  { to: '/nachhaltige-geldanlagen', title: 'Nachhaltige Geldanlagen', desc: 'Investieren mit Verantwortung' },
];

const HomePage = () => {
  return (
    <PublicShell>
      <SplitHero
        eyebrow="Sky View Asset Management"
        title={
          <>
            Ein starker Partner –<br />
            <span className="text-primary-700">Ihr größter Vorteil</span>
          </>
        }
        subtitle="Bei Sky View Asset Management wissen wir, worauf es bei einer erfolgreichen Partnerschaft ankommt. Ihre Ziele und Bedürfnisse stehen für uns an erster Stelle. Mit unserer globalen Präsenz und umfassenden Ressourcen unterstützen wir Sie dabei, Ihre finanziellen Ziele nachhaltig zu erreichen."
        graphic={<PhotoPanel src="/images/sv-skyline-day.jpg" alt="Sky View Asset Management" />}
        primaryLabel="Beratungstermin vereinbaren"
        primaryTo="/contact"
        secondaryLabel="Mehr über uns"
        secondaryTo="/about"
        stat={{ value: '700+', label: 'Anlageexperten weltweit' }}
      />

      <section className="bg-slate-50/70 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            align="center"
            eyebrow="Unsere Reichweite"
            title="Wir haben die Reichweite, um Ihre Möglichkeiten zu verwirklichen"
            className="mb-12"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <FeatureCard icon={Users} title="700+ Anlageexperten" description="Erfahrene Spezialistinnen und Spezialisten begleiten Sie weltweit bei Ihren Anlageentscheidungen." />
            <FeatureCard icon={TrendingUp} title="1,3 Mrd. € verwaltetes Vermögen" description="Umfassende Ressourcen und Administration im Dienst Ihrer finanziellen Ziele." />
            <FeatureCard icon={Globe} title="Büros in 20 Ländern" description="Eine globale Präsenz, die lokale Beratung mit internationaler Expertise verbindet." />
          </div>
        </div>
      </section>

      <SplitFeature
        icon={Shield}
        eyebrow="Verantwortung"
        title={<>Starke Partnerschaften bringen Sie <span className="text-primary-700">weiter</span></>}
        description="Wir verstehen gute Stewardship als gelebte Verantwortung — gegenüber unseren Kundinnen und Kunden, unseren Partnern und der Gesellschaft. Das umfasst unser Engagement, finanzielle Möglichkeiten zu stärken, tragfähige Strukturen aufzubauen und verantwortungsvoll zu handeln."
        graphic={<PhotoPanel src="/images/sv-handshake.jpg" alt="Starke Partnerschaften" />}
        tint
      />

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading align="center" eyebrow="Für wen wir da sind" title="Maßgeschneiderte Begleitung für jede Ausgangslage" className="mb-12" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <FeatureCard
              icon={HeartHandshake}
              title="Für Finanzintermediäre"
              description="Sie begleiten Ihre Kundinnen und Kunden auf dem Weg in eine sichere finanzielle Zukunft. Gemeinsam schaffen wir dafür die richtigen Voraussetzungen — mit Erfahrung, Zuverlässigkeit und Fokus auf individuelle Bedürfnisse."
            />
            <FeatureCard
              icon={Building2}
              title="Für Institutionen"
              description="Sie tragen Verantwortung für das finanzielle Wohl Ihrer Begünstigten, Mitarbeitenden und Gemeinschaften. Mit uns als Partner stärken Sie Ihr Risikomanagement und bauen eine widerstandsfähige Zukunft."
            />
            <FeatureCard
              icon={UserCheck}
              title="Für Privatkunden"
              description="Als Anlegerin oder Anleger erwarten Sie klare Ergebnisse und fundierte Beratung bei der Verwaltung Ihres Vermögens. Erfahren Sie, wie wir Sie dabei unterstützen können."
            />
          </div>
        </div>
      </section>

      <section className="py-4 md:py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-primary-950 border-l-4 border-gold-500 px-8 py-12 md:px-14 md:py-14 grid md:grid-cols-5 gap-10">
            <div className="md:col-span-2 flex flex-col justify-center gap-3">
              <span className="text-xs font-semibold uppercase tracking-[0.14em] text-gold-400">Unser Anspruch</span>
              <h3 className="text-2xl font-serif font-medium text-white leading-tight">Kundenorientiert. Transparent. Nachhaltig.</h3>
              <p className="text-primary-200 text-sm leading-relaxed mt-2">
                Wir begleiten Sie bei allen wichtigen Finanzfragen — persönlich, unabhängig und bundesweit. Unser Ziel ist es, gemeinsam mit Ihnen maßgeschneiderte Lösungen zu entwickeln, die zu Ihren Lebenszielen passen.
              </p>
            </div>
            <div className="md:col-span-3 grid sm:grid-cols-2 gap-3">
              {SERVICE_LINKS.map((s) => (
                <Link
                  key={s.to}
                  to={s.to}
                  className="group flex items-start justify-between gap-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-gold-500/40 px-5 py-4 transition-colors"
                >
                  <span>
                    <span className="block text-sm font-semibold text-white">{s.title}</span>
                    <span className="block text-xs text-primary-300 mt-1">{s.desc}</span>
                  </span>
                  <ArrowRight className="w-4 h-4 text-primary-400 group-hover:text-gold-400 shrink-0 mt-0.5 transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaCard
        title="Bereit für den nächsten Schritt?"
        subtitle="Vereinbaren Sie ein unverbindliches Beratungsgespräch — wir nehmen uns Zeit für Ihre individuelle Situation."
        primaryLabel="Beratungstermin vereinbaren"
        primaryTo="/contact"
        secondaryLabel="Über uns"
        secondaryTo="/about"
      />
    </PublicShell>
  );
};

export default HomePage;
